import React, { useState } from "react";
import { PromptOutput } from "./types";
import { HashLoader } from "react-spinners";
import { FaCopy, FaFileExport, FaFileWord, FaDownload } from "react-icons/fa";
import { BiExport } from "react-icons/bi";
import { toast } from "react-hot-toast";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

interface PromptResultProps {
  outputs: PromptOutput[];
  result: any[];
  isLoading?: boolean;
}

const PromptResult: React.FC<PromptResultProps> = ({
  outputs,
  result,
  isLoading = false,
}) => {
  const [exportMenuOpen, setExportMenuOpen] = useState(false);

  const renderOutput = (output: PromptOutput, value: any) => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-4">
          <HashLoader size={20} color="#4B5563" />
        </div>
      );
    }

    if (value === undefined || value === null) {
      return <p className="text-gray-500 italic">No result available</p>;
    }

    switch (output.type) {
      case "array":
        if (!Array.isArray(value)) {
          return (
            <p className="text-red-500">
              Error: Expected array but got {typeof value}
            </p>
          );
        }
        return (
          <ul className="list-disc pl-5 space-y-1">
            {value.map((item: any, index: number) => (
              <li key={index} className="text-gray-700">
                {typeof item === "object" ? JSON.stringify(item) : item}
              </li>
            ))}
          </ul>
        );
      case "object":
        if (typeof value !== "object" || value === null) {
          return (
            <p className="text-red-500">
              Error: Expected object but got {typeof value}
            </p>
          );
        }
        return (
          <pre className="bg-gray-50 p-4 rounded-md overflow-x-auto">
            {JSON.stringify(value, null, 2)}
          </pre>
        );
      default:
        // Check if the value is a string that might contain markdown
        if (
          typeof value === "string" &&
          (value.includes("#") || value.includes("*") || value.includes("`"))
        ) {
          return (
            <div className="prose prose-sm max-w-none prose-headings:text-gray-800 prose-p:text-gray-700 prose-strong:text-gray-800 prose-code:text-gray-700 prose-code:bg-gray-100 prose-code:px-1 prose-code:rounded prose-pre:bg-gray-100 prose-pre:text-gray-800 prose-pre:border prose-pre:border-gray-200 prose-pre:rounded-md prose-pre:p-4 prose-pre:overflow-x-auto prose-ul:list-disc prose-ul:pl-5 prose-ol:list-decimal prose-ol:pl-5 prose-li:text-gray-700 prose-blockquote:border-l-4 prose-blockquote:border-gray-300 prose-blockquote:pl-4 prose-blockquote:text-gray-600">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{value}</ReactMarkdown>
            </div>
          );
        }
        return <p className="text-gray-700">{String(value)}</p>;
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard!");
    } catch (err) {
      toast.error("Failed to copy to clipboard");
      console.error("Failed to copy: ", err);
    }
  };

  const exportToWordDoc = (content: string) => {
    const html = `
      <html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word'>
      <head>
        <meta charset="utf-8">
        <title>Results</title>
        <style>
          body { font-family: 'Calibri', sans-serif; }
          .result { margin-bottom: 20px; }
        </style>
      </head>
      <body>
        ${content}
      </body>
      </html>
    `;

    const blob = new Blob([html], { type: "application/msword" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "results.doc";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Word document downloaded!");
  };

  const exportToPDF = (content: string) => {
    const printWindow = window.open("", "_blank");
    if (printWindow) {
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <title>Results</title>
          <style>
            body { font-family: Arial, sans-serif; }
            .result { margin-bottom: 15px; }
            @media print { @page { margin: 1cm; } }
          </style>
        </head>
        <body>
          ${content}
        </body>
        </html>
      `);
      printWindow.document.close();
      setTimeout(() => {
        printWindow.print();
        printWindow.onafterprint = () => printWindow.close();
      }, 500);
      toast.success("PDF prepared for printing!");
    } else {
      toast.error("Please allow popups to enable PDF export");
    }
  };

  const exportToText = (content: string) => {
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "results.txt";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Text file downloaded!");
  };

  if (!outputs || !Array.isArray(outputs)) {
    return <p className="text-red-500">Error: Invalid outputs configuration</p>;
  }

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Results</h2>
        <div className="flex items-center justify-center py-8">
          <HashLoader size={30} color="#4B5563" />
        </div>
      </div>
    );
  }

  if (!result || !Array.isArray(result) || result.length === 0) {
    return <p className="text-red-500">Error: Invalid result data</p>;
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-800">Results</h2>
        <div className="relative">
          <button
            onClick={() => setExportMenuOpen(!exportMenuOpen)}
            className="px-4 py-2 bg-blue-100 hover:bg-blue-200 text-blue-800 font-medium rounded-lg flex items-center transition-colors"
          >
            <BiExport className="mr-2" />
            Export
          </button>

          {exportMenuOpen && (
            <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg z-10 overflow-hidden border border-gray-100">
              <div className="py-1">
                <button
                  onClick={() => {
                    const text = result
                      .map((item, index) =>
                        outputs
                          .map(
                            (output) => `${output.name}: ${item[output.name]}`
                          )
                          .join("\n")
                      )
                      .join("\n\n");
                    copyToClipboard(text);
                    setExportMenuOpen(false);
                  }}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                >
                  <FaCopy className="mr-2" />
                  Copy all to clipboard
                </button>
                <button
                  onClick={() => {
                    const content = result
                      .map(
                        (item, index) =>
                          `<div class="result">
                        ${outputs
                          .map(
                            (output) =>
                              `<h3>${output.name}</h3>
                           <p>${item[output.name]}</p>`
                          )
                          .join("")}
                      </div>`
                      )
                      .join("");
                    exportToWordDoc(content);
                    setExportMenuOpen(false);
                  }}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                >
                  <FaFileWord className="mr-2" />
                  Export as Word Doc
                </button>
                <button
                  onClick={() => {
                    const content = result
                      .map(
                        (item, index) =>
                          `<div class="result">
                        ${outputs
                          .map(
                            (output) =>
                              `<h3>${output.name}</h3>
                           <p>${item[output.name]}</p>`
                          )
                          .join("")}
                      </div>`
                      )
                      .join("");
                    exportToPDF(content);
                    setExportMenuOpen(false);
                  }}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                >
                  <FaDownload className="mr-2" />
                  Export as PDF
                </button>
                <button
                  onClick={() => {
                    const content = result
                      .map((item, index) =>
                        outputs
                          .map(
                            (output) => `${output.name}:\n${item[output.name]}`
                          )
                          .join("\n\n")
                      )
                      .join("\n\n---\n\n");
                    exportToText(content);
                    setExportMenuOpen(false);
                  }}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                >
                  <FaFileExport className="mr-2" />
                  Export as Text
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="space-y-8">
        {result.map((resultItem, index) => (
          <div
            key={index}
            className="bg-gray-50 rounded-lg p-6 border border-gray-200"
          >
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-lg font-medium text-gray-800">
                Result {index + 1}
              </h3>
              <button
                onClick={() => {
                  const text = outputs
                    .map(
                      (output) => `${output.name}: ${resultItem[output.name]}`
                    )
                    .join("\n");
                  copyToClipboard(text);
                }}
                className="p-2 text-gray-500 hover:text-gray-700 focus:outline-none"
                title="Copy to clipboard"
              >
                <FaCopy size={18} />
              </button>
            </div>
            <div className="space-y-4">
              {outputs.map((output) => (
                <div key={output.name} className="space-y-2">
                  <h4 className="font-medium text-gray-700">{output.name}</h4>
                  <p className="text-sm text-gray-500 mb-1">
                    {output.description}
                  </p>
                  <div className="bg-white p-4 rounded-md border border-gray-200">
                    {renderOutput(output, resultItem[output.name])}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PromptResult;
